import * as yup from "yup";
import { PHONE_MIN_LENGTH, PHONE_MAX_LENGTH } from "../../constants/phone";

const PHONE_LENGTH_MESSAGE = `Phone Number must be between ${PHONE_MIN_LENGTH} and ${PHONE_MAX_LENGTH} digits`;

// Shared phone-number test. Phone length is uniform across the platform
// (digits only, PHONE_MIN_LENGTH-PHONE_MAX_LENGTH characters) and no longer tied
// to the selected country.
function validatePhoneLength(value) {
  if (!value) return true;

  if (!/^\d+$/.test(value)) {
    return this.createError({ message: "Phone Number must contain only digits" });
  }

  if (value.length < PHONE_MIN_LENGTH || value.length > PHONE_MAX_LENGTH) {
    return this.createError({ message: PHONE_LENGTH_MESSAGE });
  }

  return true;
}

export const step1schema = yup.object().shape({
  administrativeRegion: yup
    .string()
    .trim()
    .required('Country is required'),
  registrationType: yup
    .string()
    .oneOf(['individual', 'organization'])
    .required('Registration type is required'),
  userTypeId: yup
    .string()
    .trim()
    .required('User Type is required'),
  firstName: yup.string().when('registrationType', {
    is: 'individual',
    then: (schema) => schema.required('First Name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  lastName: yup.string().when('registrationType', {
    is: 'individual',
    then: (schema) => schema.required('Last Name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  organisationName: yup.string().when('registrationType', {
    is: 'organization',
    then: (schema) => schema.required('Organization Name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required")
    .test(
      "valid-domain",
      "Invalid email format",
      (value) => {
        if (!value) return true;
        return /\.(com|co|in)$/i.test(value);
      }
    ),
  countryCode: yup.string().required('Country Code is required'),
  phoneNumber: yup
    .string()
    .required('Phone Number is required')
    .test('is-valid-phone', validatePhoneLength),
  termsAndPolicies: yup.boolean().oneOf([true], "You must accept the terms"),
  serviceProviderType: yup.string().optional()
    .when("userType",
      {
        is: "Service Provider",
        then: (schema) => schema.required("Service Provider Type is required"),
      }
    ),
  // eK Score Customer: organisation type always; organisation name here only for an individual
  // (an organisation registration already gives it in organisationName).
  scoreCustomerType: yup.string().optional()
    .when("userType", {
      is: (value) => String(value || '').toUpperCase() === 'SCORE_CUSTOMER',
      then: (schema) => schema.required("Organisation type is required"),
    }),
  scoreCustomerOrganizationName: yup.string().trim().optional()
    .when(["userType", "registrationType"], {
      is: (userType, registrationType) =>
        String(userType || '').toUpperCase() === 'SCORE_CUSTOMER' && registrationType !== 'organization',
      then: (schema) => schema.required("Organisation name is required"),
    }),
});

export const step2Schema = yup.object().shape({
  addressLine1: yup.string().required("Address Line 1 is required"),
  addressLine2: yup.string().optional(),
  level1: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required("Level 1 is required"),
  level2: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required("Level 2 is required"),
  level3: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required("Level 3 is required"),
  level4: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .notRequired(),
  pincode: yup
    .string()
    .required("Postal Code is required")
    .min(5, "Postal Code must be at least 5 digits")
    .max(10, "Postal Code must be at most 10 digits"),
});

// Builds the Step 2 (Address) schema from the country's address template so the
// required levels track the template. The LAST administrative level is always
// optional; every earlier level follows its template `required` flag (defaulting
// to required). Falls back to the static level1-3 rules when no template levels.
export const buildStep2Schema = (levels = []) => {
  const shape = {
    addressLine1: yup.string().required("Address Line 1 is required"),
    addressLine2: yup.string().optional(),
    pincode: yup
      .string()
      .required("Postal Code is required")
      .min(5, "Postal Code must be at least 5 digits")
      .max(10, "Postal Code must be at most 10 digits"),
  };

  if (!Array.isArray(levels) || levels.length === 0) {
    return step2Schema;
  }

  const lastLevelNumber = levels.length;

  levels.forEach((level) => {
    const fieldName = `level${level.level}`;
    const isLastLevel = level.level === lastLevelNumber;

    let fieldSchema = yup
      .string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value));

    if (!isLastLevel && level.required !== false) {
      fieldSchema = fieldSchema.required(`${level.name || `Level ${level.level}`} is required`);
    } else {
      fieldSchema = fieldSchema.notRequired();
    }

    shape[fieldName] = fieldSchema;
  });

  return yup.object().shape(shape);
};

export const step3Schema = yup.object().shape({
  preferredEcosystemTypes: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one preferred ecosystem type"),
  // Initiative approval chain: a DARE may pick one Initiative its area integrator offers, or none and take
  // one up later; other personas pick at least one. Pass { context: { isDare: true } } for a DARE.
  initiativeEcosystemToJoin: yup
    .array()
    .of(yup.mixed().required())
    .when('$isDare', {
      is: true,
      then: (schema) => schema.max(1, "Pick one Initiative"),
      otherwise: (schema) => schema.min(1, "Select at least one initiative to continue"),
    }),
  // EK-02: a DARE whose country has no Ecosystem Integrator may continue without one (eKutir
  // reviews it); pass { context: { integratorsOptional: true } } for that case.
  selectedIntegrators: yup
    .array()
    .of(yup.mixed().required())
    .when('$integratorsOptional', {
      is: true,
      then: (schema) => schema,
      otherwise: (schema) => schema.min(1, "Select at least one integrator to continue"),
    }),
});

export const signupStep4Schema = yup.object().shape({
  subscriptionPlanId: yup
    .mixed()
    .required("Select a subscription plan"),
});

export const signupPaymentSchema = yup.object().shape({
  preferredPaymentMethodId: yup
    .mixed()
    .required("Select a payment method"),
  paymentCardNumber: yup.string().when(['preferredPaymentMethodType', 'preferredPaymentProvider'], {
    is: (type, provider) => type === 'card' && provider !== 'stripe',
    then: (schema) =>
      schema
        .required('Card number is required')
        .matches(/^\d{16}$/, 'Card number must be exactly 16 digits'),
    otherwise: (schema) => schema.notRequired(),
  }),
  paymentCardExpiry: yup.string().when(['preferredPaymentMethodType', 'preferredPaymentProvider'], {
    is: (type, provider) => type === 'card' && provider !== 'stripe',
    then: (schema) =>
      schema
        .required('Expiry date is required')
        .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Use MM/YY format'),
    otherwise: (schema) => schema.notRequired(),
  }),
  paymentCardCvc: yup.string().when(['preferredPaymentMethodType', 'preferredPaymentProvider'], {
    is: (type, provider) => type === 'card' && provider !== 'stripe',
    then: (schema) =>
      schema
        .required('CVC is required')
        .matches(/^\d{3,4}$/, 'CVC must be 3 or 4 digits'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const signupReviewSchema = yup.object().shape({
  reviewConfirmed: yup
    .boolean()
    .oneOf([true], 'Please confirm your details before placing the order'),
});

export const financialSupportApplicationSchema = yup.object().shape({
  financialSupportType: yup.string().required('Type is required'),
  financialSupportPlanId: yup.string().required('Select a financial assistance plan'),
  financialSupportCoveragePercentage: yup
    .number()
    .min(0, 'Coverage percentage must be between 0 and 100')
    .max(100, 'Coverage percentage must be between 0 and 100')
    .nullable(),
  financialSupportCoverageAmount: yup.number().min(0).nullable(),
  financialSupportUserPaymentAmount: yup.number().min(0).nullable(),
});

export const step4Schema = yup.object().shape({
  phoneNumber: yup
    .string()
    .required("Please enter a valid phone number")
    .test("is-valid-phone", validatePhoneLength),
  countryCode: yup.string().required("Country Code is required"),
});

export const step5Schema = yup.object().shape({
  otp: yup
    .string()
    .required("OTP is required")
    .matches(/^\d{4}$/, "OTP must be exactly 4 digits"),
});
